/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import '@/quote-management/components/OrderSummary/OrderSummary.css';
import { OrderSummaryLine } from '@/quote-management/containers/OrderSummaryLine/OrderSummaryLine';
import {
  Accordion,
  AccordionSection,
  Divider,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { ChevronDown, ChevronUp } from '@adobe-commerce/elsie/icons';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useState } from 'preact/compat';
import { JSXInternal } from 'preact/src/jsx';

export interface OrderSummaryLineItem {
  key: string;
  title?: string;
  className?: string;
  sortOrder: number;
  content:
    | string
    | JSXInternal.Element
    | VNode<HTMLAttributes<HTMLDivElement>>
    | OrderSummaryLineItem[]
    | undefined;
}

export interface OrderSummaryProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary';
  heading?: VNode<HTMLAttributes<HTMLDivElement>>;
  subTotal?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceExcludingTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
    taxIncluded?: boolean;
    taxExcluded?: boolean;
  };
  shipping?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceExcludingTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
    taxIncluded?: boolean;
    taxExcluded?: boolean;
  };
  discounts?: {
    label: string;
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    caption?: VNode<HTMLAttributes<HTMLDivElement>>;
    coupon?: VNode<HTMLAttributes<HTMLSpanElement>>;
  }[];
  taxTotal?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  taxesApplied?: {
    label: string;
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
  }[];
  total?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceWithoutTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  totalSaved?: VNode<HTMLAttributes<HTMLSpanElement>>;
  updateLineItems?: (
    lineItems: Array<OrderSummaryLineItem>
  ) => Array<OrderSummaryLineItem>;
}

export const OrderSummary: FunctionComponent<OrderSummaryProps> = ({
  className,
  children,
  variant = 'primary',
  heading,
  subTotal,
  shipping,
  discounts,
  taxTotal,
  taxesApplied,
  total,
  totalSaved,
  updateLineItems = (lineItems) => lineItems,
  ...props
}) => {
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  const translations = useText({
    // Taxes
    taxesBreakdownTitle: 'NegotiableQuote.PriceSummary.taxes.breakdown',
    taxTotal: 'NegotiableQuote.PriceSummary.taxes.total',
    taxTotalOnly: 'NegotiableQuote.PriceSummary.taxes.totalOnly',
    showTaxBreakdown: 'NegotiableQuote.PriceSummary.taxes.showBreakdown',
    hideTaxBreakdown: 'NegotiableQuote.PriceSummary.taxes.hideBreakdown',

    // Subtotal
    subtotalLabel: 'NegotiableQuote.PriceSummary.subTotal.label',
    subtotalWithTaxes: 'NegotiableQuote.PriceSummary.subTotal.withTaxes',
    subtotalWithoutTaxes: 'NegotiableQuote.PriceSummary.subTotal.withoutTaxes',

    // Shipping
    shippingLabel: 'NegotiableQuote.PriceSummary.shipping.label',
    shippingWithTaxes: 'NegotiableQuote.PriceSummary.shipping.withTaxes',
    shippingWithoutTaxes: 'NegotiableQuote.PriceSummary.shipping.withoutTaxes',

    // Total
    totalLabel: 'NegotiableQuote.PriceSummary.total.label',
    totalWithoutTax: 'NegotiableQuote.PriceSummary.total.withoutTax',
    totalSaved: 'NegotiableQuote.PriceSummary.total.saved',
  });

  const subTotalContent = subTotal && (
    <OrderSummaryLine
      key={translations.subtotalLabel}
      label={translations.subtotalLabel}
      price={subTotal.price}
      classSuffixes={['subTotal']}
    >
      {subTotal.taxIncluded && (
        <div
          data-testid="sub-total-tax-caption"
          className="quote-order-summary__caption"
        >
          <span>{translations.subtotalWithTaxes}</span>
        </div>
      )}

      {subTotal.taxExcluded ? (
        <div
          data-testid="sub-total-tax-caption-excluded"
          className="quote-order-summary__caption"
        >
          <span>
            {subTotal.priceExcludingTax}
            &nbsp;
            {translations.subtotalWithoutTaxes}
          </span>
        </div>
      ) : undefined}
    </OrderSummaryLine>
  );

  const shippingContent = shipping && (
    <OrderSummaryLine
      key={translations.shippingLabel}
      label={translations.shippingLabel}
      price={shipping.price}
      classSuffixes={['shipping']}
    >
      {shipping.taxIncluded && (
        <div
          data-testid="shipping-tax-caption"
          className="quote-order-summary__caption"
        >
          <span>{translations.shippingWithTaxes}</span>
        </div>
      )}

      {shipping.taxExcluded ? (
        <div
          data-testid="shipping-tax-caption-excluded"
          className="quote-order-summary__caption"
        >
          <span>
            {shipping.priceExcludingTax}
            &nbsp;
            {translations.shippingWithoutTaxes}
          </span>
        </div>
      ) : undefined}
    </OrderSummaryLine>
  );

  const discountsContent = discounts && discounts.length > 0 && (
    <>
      {discounts.map((discount) => (
        <OrderSummaryLine
          key={discount.label}
          label={discount.label}
          price={discount.price}
          classSuffixes={['discount']}
        >
          {discount.coupon && (
            <VComponent
              node={discount.coupon}
              className="quote-order-summary__coupon__code"
            />
          )}

          {discount.caption && (
            <VComponent
              node={discount.caption}
              className="quote-order-summary__caption"
            />
          )}
        </OrderSummaryLine>
      ))}
    </>
  );

  const taxContent =
    taxesApplied && taxesApplied.length > 0 ? (
      /* Show breakdown if provided */
      <Accordion
        data-testid="tax-breakdown"
        className="quote-order-summary__taxes"
        iconOpen={ChevronDown}
        iconClose={ChevronUp}
      >
        <AccordionSection
          ariaLabelTitle={translations.taxesBreakdownTitle}
          title={translations.taxesBreakdownTitle}
          secondaryText={
            !showTaxBreakdown && taxTotal ? (
              <VComponent
                node={taxTotal.price}
                className="quote-order-summary__price"
              />
            ) : undefined
          }
          renderContentWhenClosed={false}
          onStateChange={setShowTaxBreakdown}
        >
          <div className="quote-order-summary__appliedTaxes">
            {taxesApplied.map((tax) => (
              <OrderSummaryLine
                key={tax.label}
                label={tax.label}
                price={tax.price}
                classSuffixes={['taxEntry']}
                labelClassSuffix="muted"
              />
            ))}
          </div>

          {taxTotal && (
            <OrderSummaryLine
              label={translations.taxTotal}
              price={taxTotal.price}
              classSuffixes={['taxTotal']}
              labelClassSuffix="muted"
            />
          )}
        </AccordionSection>
      </Accordion>
    ) : (
      taxTotal && (
        <OrderSummaryLine
          label={translations.taxTotalOnly}
          price={taxTotal.price}
          classSuffixes={['taxTotal']}
          testId="tax-total-only"
        />
      )
    );

  const lineItems: Array<OrderSummaryLineItem> = [
    {
      key: 'subTotalContent',
      sortOrder: 100,
      content: subTotalContent,
    },
    {
      key: 'shippingContent',
      sortOrder: 200,
      content: shippingContent,
    },
    {
      key: 'discountsContent',
      sortOrder: 600,
      content: discountsContent,
    },
    {
      key: 'taxContent',
      sortOrder: 800,
      content: taxContent,
    },
    ...(total
      ? [
          {
            key: 'totalContent',
            sortOrder: 900,
            content: (
              <OrderSummaryLine
                label={translations.totalLabel}
                price={total.price}
                classSuffixes={['total', 'total--padded']}
                testId="total-content"
                labelClassSuffix="bold"
              />
            ),
          },
        ]
      : []),
    ...(total?.priceWithoutTax
      ? [
          {
            key: 'totalWithoutTaxContent',
            sortOrder: 1000,
            content: (
              <OrderSummaryLine
                label={translations.totalWithoutTax}
                price={total.priceWithoutTax}
                classSuffixes={['totalWithoutTax']}
                testId="total-without-tax"
                labelClassSuffix="muted"
              />
            ),
          },
        ]
      : []),
    ...(totalSaved
      ? [
          {
            key: 'totalSavedContent',
            sortOrder: 1100,
            content: (
              <OrderSummaryLine
                label={translations.totalSaved}
                price={totalSaved}
                classSuffixes={['saved']}
                testId="total-saved"
                labelClassSuffix="muted"
              />
            ),
          },
        ]
      : []),
  ];

  const updatedLineItems: Array<OrderSummaryLineItem> = updateLineItems(
    lineItems
  ).sort(
    (currentLine: OrderSummaryLineItem, nextLine: OrderSummaryLineItem) => {
      return currentLine.sortOrder - nextLine.sortOrder;
    }
  );

  return (
    <div
      {...props}
      className={classes([
        'quote-order-summary',
        [`quote-order-summary__${variant}`, variant],
        className,
      ])}
    >
      <div className="quote-order-summary__heading">
        {heading && (
          <VComponent
            node={heading}
            className="quote-order-summary__heading-text"
          />
        )}

        <Divider
          variant="primary"
          className="quote-order-summary__divider-primary"
        />
      </div>

      <div className="quote-order-summary__content">
        {updatedLineItems.map((line: OrderSummaryLineItem) => {
          if (Array.isArray(line.content)) {
            return (
              <Accordion
                key={line.key}
                className={line.className}
                actionIconPosition={'right'}
                iconOpen={ChevronDown}
                iconClose={ChevronUp}
              >
                <AccordionSection
                  defaultOpen={false}
                  title={line.title}
                  renderContentWhenClosed={false}
                >
                  {line.content.map((innerLine) => {
                    return innerLine.content;
                  })}
                </AccordionSection>
              </Accordion>
            );
          }
          return line.content;
        })}
      </div>
    </div>
  );
};
